import { useState } from "react";
import {
  ConnectionHandler,
  Environment,
  graphql,
  useMutation,
} from "react-relay";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import EventCompetitions from "@/components/EventCompetitions";
import { EventCompetitionsPageCreateMutation as EventCompetitionsPageCreateMutationType } from "./__generated__/EventCompetitionsPageCreateMutation.graphql";
import CompetitionAutocomplete from "@/components/CompetitionAutocomplete";
import { logger } from "@/common/logger";
import { Button } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import { generatePath } from "react-router";
import { LoaderArgs } from "@/utils/route";
import { EventCompetitionsPageQuery as EventCompetitionsPageQueryType } from "./__generated__/EventCompetitionsPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
import { ActivityNotFound } from "@/components/ActivityNotFound";
const EventCompetitionsPageCreateMutation = graphql`
  mutation EventCompetitionsPageCreateMutation(
    $eventId: ID!
    $competitionId: ID!
    $connections: [ID!]!
  ) {
    addEventCompetition(eventId: $eventId, competitionId: $competitionId)
      @prependEdge(connections: $connections) {
      node {
        id
        viewerCanDelete: viewerCan(action: REMOVE_EVENT_COMPETITION)
        membersCanReadEvent
        competition {
          id
          slug
          title
          isPrivate
          ...CompetitionCardFragment
        }
      }
    }
  }
`;
type FormData = {
  competitionId: string;
};
export default function EventCompetitionsPage() {
  const intl = useIntl();
  const { query } = useEventCompetitionsPageQuery();
  const event = query.eventBySlug;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();
  const [formError, setFormError] = useState<string | null>(null);
  const [commitAddMutation, isAddMutationInFlight] =
    useMutation<EventCompetitionsPageCreateMutationType>(
      EventCompetitionsPageCreateMutation,
    );
  if (!event) {
    return <ActivityNotFound kind="event" />;
  }
  const errorMessages = {
    competitionId: {
      required: intl.formatMessage({
        defaultMessage: "Please select the username from the list",
      }),
    },
  };
  const onAddSubmit = handleSubmit(({ competitionId }) => {
    setFormError(null);
    commitAddMutation({
      variables: {
        eventId: event.id,
        competitionId,
        connections: [
          ConnectionHandler.getConnectionID(
            event.id,
            "EventCompetitionsFragment_competitions",
          ),
          ConnectionHandler.getConnectionID(
            event.id,
            "EventCompetitionsCarouselFragment_competitions",
          ),
        ],
      },
      onError: (error) => {
        logger.error(error);
        setFormError(
          intl.formatMessage({ defaultMessage: "Something went wrong" }),
        );
      },
    });
  });
  return (
    <>
      {event.viewerCanEditCompetitions && (
        <div className="flex flex-col items-end pb-8">
          <form>
            <div className="flex flex-row space-x-2">
              <div>
                <CompetitionAutocomplete
                  query={query}
                  placeholder={intl.formatMessage({
                    defaultMessage: "track-slug",
                  })}
                  {...register("competitionId", { required: true })}
                />
              </div>
              <div className="flex flex-row items-center gap-2">
                <Button
                  onClick={() => {
                    onAddSubmit();
                  }}
                  disabled={isAddMutationInFlight}
                >
                  <span className="sm:hidden">
                    <FormattedMessage defaultMessage="Add" />
                  </span>
                  <span className="hidden sm:inline">
                    <FormattedMessage defaultMessage="Add Track" />
                  </span>
                </Button>
                <Link
                  size="md"
                  variant="secondary"
                  to={generatePath("/challenges/:owner/tracks/new", {
                    owner: event.slug,
                  })}
                  className="text-sm"
                >
                  <FormattedMessage defaultMessage="Create Track" />
                </Link>
              </div>
            </div>
          </form>
          <div>
            {typeof errors.competitionId?.type === "string" && (
              <p className="text-red-500 text-sm">
                {
                  errorMessages.competitionId[
                    errors.competitionId
                      .type as keyof typeof errorMessages.competitionId
                  ]
                }
              </p>
            )}
            {formError && <p className="text-red-500 text-sm">{formError}</p>}
          </div>
        </div>
      )}
      <div>
        <EventCompetitions event={event} />
      </div>
    </>
  );
}
const EventCompetitionsPageQuery = graphql`
  query EventCompetitionsPageQuery($slug: String!) {
    ...CompetitionAutocompleteFragment
    eventBySlug(slug: $slug) {
      id
      slug
      title
      viewerCanEditCompetitions: viewerCan(action: ADD_EVENT_COMPETITION)
      ...EventCompetitionsFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { slug } }: LoaderArgs) => {
    if (!slug) {
      throw new Response("No slug", { status: 500 });
    }
    return preload<EventCompetitionsPageQueryType>(
      environment,
      EventCompetitionsPageQuery,
      {
        slug,
      },
    );
  };
};
const useEventCompetitionsPageQuery = () =>
  usePreloaded<EventCompetitionsPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Tracks" }),
};
